const formatDate = (date) => {
    if (!(date instanceof Date)) {
        throw new Error('Se esperaba un objeto Date.');
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const fetchUserDevices = async (userHash) => {
    try {
        const response = await fetch('https://pro.lepton-seguridad.com/api-v2/tracker/list', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                hash: userHash,
                sort: ["label=asc"],
            }),
        });

        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            throw new Error('Error al obtener información de los dispositivos.');
        }
    } catch (error) {
        // console.error('Error al conectar con el servidor:', error);
        throw error; // Lanzar el error para manejarlo más arriba
    }
};

export const fetchTrackersGroups = async (userHash) => {
    try {
        const response = await fetch('https://pro.lepton-seguridad.com/api-v2/tracker/group/list', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                hash: userHash
            }),
        });

        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            throw new Error('Error al obtener información de los grupos.');
        }
    } catch (error) {
        // console.error('Error al conectar con el servidor:', error);
        throw error; // Lanzar el error para manejarlo más arriba
    }
};

const MAX_RETRIES = 5; // Número máximo de reintentos permitido

export const fetchGenerateReport = async (userHash, trackersArray, startDate, endDate, plugin, title, retries = 0) => {
    try {
        const today = new Date();
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setDate(today.getDate() - 59);

        const startOfMonthAgo = formatDate(new Date(oneMonthAgo.getFullYear(), oneMonthAgo.getMonth(), oneMonthAgo.getDate(), 0, 0, 0));
        const endOfToday = formatDate(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59));

        const response = await fetch('https://pro.lepton-seguridad.com/api-v2/report/tracker/generate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                hash: userHash,
                title: title,
                from: formatDate(startDate),
                to: formatDate(endDate),
                geocoder: 'google',
                time_filter: { from: '00:00:00', to: '23:59:59', weekdays: [1, 2, 3, 4, 5, 6, 7] },
                plugin: plugin,
                trackers: trackersArray
            }),
        });

        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            const errorData = await response.json();
            if (errorData.status.code === 15) { // Código 15 para "Too Many Requests"
                if (retries < MAX_RETRIES) {
                    console.warn(`Reintento ${retries + 1} de ${MAX_RETRIES}: Demasiadas solicitudes. Obteniendo nuevo ID...`);
                    return await fetchNewReportId(userHash, trackersArray, startDate, endDate, plugin, title, retries + 1);
                } else {
                    throw new Error('Se alcanzó el máximo número de reintentos para generar el reporte.');
                }
            }
            throw new Error('Error al obtener el reporte.');
        }
    } catch (error) {
        // console.error('Error al conectar con el servidor:', error);
        throw error;
    }
};

export const fetchNewReportId = async (userHash, trackersArray, startDate, endDate, plugin, title, retries = 0) => {
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms)); // Función de espera

    try {
        const response = await fetch('https://pro.lepton-seguridad.com/api-v2/report/tracker/list', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                hash: userHash
            }),
        });

        if (response.ok) {
            const data = await response.json();

            // Filtrar la lista donde 'title' sea igual a la variable 'title' y 'percent' sea menor que 100
            const filteredList = data.list.filter(item => item.title === title && item.percent < 100);

            if (filteredList.length > 0) {
                return filteredList[0]; // Retorna el primer elemento que cumple las condiciones
            } else {
                if (retries < MAX_RETRIES) {
                    console.warn(`Reintento ${retries + 1} de ${MAX_RETRIES}: No se encontró ningún reporte. Intentando de nuevo en 5 segundos...`);
                    await delay(5000); // Espera 5 segundos
                    return await fetchGenerateReport(userHash, trackersArray, startDate, endDate, plugin, title, retries + 1); // Reintenta generar el reporte
                } else {
                    throw new Error('Se alcanzó el máximo número de reintentos para obtener el reporte.');
                }
            }
        } else {
            throw new Error('Error al obtener el nuevo ID de reporte.');
        }
    } catch (error) {
        // console.error('Error al conectar con el servidor:', error);
        throw error;
    }
};

export const checkReportStatus = async (userHash, reportID, onReady, onProgress) => {
    try {
        const statusResponse = await fetch(`https://pro.lepton-seguridad.com/api-v2/report/tracker/status`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                hash: userHash,
                report_id: reportID,
            }),
        });

        if (!statusResponse.ok) {
            throw new Error(`Error en la respuesta del servidor: ${statusResponse.statusText}`);
        }

        const statusData = await statusResponse.json();
        if (statusResponse.ok) {
            if (statusData.percent_ready === 100) {
                onReady();
            } else {
                // Actualiza el progreso
                if (onProgress) {
                    onProgress(statusData.percent_ready); // Pasa el porcentaje al callback
                }

                // Esperar un segundo antes de volver a consultar
                setTimeout(() => checkReportStatus(userHash, reportID, onReady, onProgress), 1000);
            }
        } else {
            // console.error('Error al consultar el estado del reporte:', statusData);
            throw new Error(`Error en la respuesta del servidor: ${statusResponse.statusText}`);
        }

    } catch (error) {
        throw error;
    }
};



export const fetchRetrieveReport = async (userHash, reportID) => {
    try {
        if (!reportID) {
            throw new Error('El reportID no es válido.');
        }

        const response = await fetch('https://pro.lepton-seguridad.com/api-v2/report/tracker/retrieve', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                hash: userHash,
                report_id: reportID
            }),
        });

        const data = await response.json();
        if (!response.ok) {
            if (data.code === 229) {
                // Puedes manejar el código 229 aquí si deseas
                return { success: false, code: 229, message: 'Requested data is not ready yet' };
            }
            throw new Error('Error al leer el reporte del día de hoy.');
        }
        return data;
    } catch (error) {
        // console.error('Error al conectar con el servidor:', error);
        throw error; // Lanzar el error para manejarlo más arriba
    }
};

export const deleteReport = async (userHash, reportId) => {
    try {
        const response = await fetch('https://pro.lepton-seguridad.com/api-v2/report/tracker/delete', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                hash: userHash,
                report_id: reportId
            }),
        });

        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            throw new Error('Error al eliminar el reporte.');
        }
    } catch (error) {
        // console.error('Error al conectar con el servidor:', error);
        throw error;
    }
};


// ---------------------------- API de Leptón ---------------------------------------

const apiDashboardUrl = 'https://dashboard-api.lepton-seguridad.com';
// const apiDashboardUrl = 'http://localhost:3103';

export const fetchUserUpdate = async (userHash, userConstants) => {
    try {
      const response = await fetch(`${apiDashboardUrl}/users`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          hash: userHash,
          userConstants,
        }),
      });
  
      if (!response.ok) {
        throw new Error('Error al actualizar datos en la base de datos.');
      }
  
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error en fetchUsersUpdate:', error);
      throw error;
    }
  };
  
